import { ClientDataType } from "@/api/ClientApi";
import { CreditnoteDataType } from "@/api/CreditnoteApi";
import { CurrencyDataType } from "@/api/CurrencyApi";
import { InvoiceDataType } from "@/api/InvoiceApi";
import { InvoiceTemplateDataType } from "@/api/InvoiceTemplateApi";
import { ProductExemptionDataType } from "@/formTypes/ProductExemptionDataType";
import { ProductTaxChangeDataType } from "@/formTypes/ProductTaxChangeDataType";
import { DocumentsEntryType } from "@components/dynamic/DocumentStatusList";
import { EmailProps } from "@components/forms/SendToEmailForm";
import { CreditAmountTarget } from "@utils/CreditAmount";
import { AccountingPackageMode } from "@utils/accountingPackage/reports";
import { ReactNode } from "react";

// src/utils/pubsub.ts
type Callback<T> = (data: T) => void;

class PubSub<T> {
    private subscribers = new Set<Callback<T>>();

    subscribe(cb: Callback<T>) {
        this.subscribers.add(cb);
        return () => this.subscribers.delete(cb); // unsubscribe function
    }

    publish(data: T,) {
        this.subscribers.forEach(cb => cb(data));
    }
}

export type CurrencyUpdatePubSubPayload = {
    currencyId: string,
    forceUpload?: boolean
}

export const CurrencyIdPubSub = new PubSub<CurrencyUpdatePubSubPayload>();

export type OpenAddProductPubSubPayload = {
    openOffCanvas?: boolean
}

export const OpenAddProductPubSub = new PubSub<OpenAddProductPubSubPayload>();

export type OpenSearchClientByIdentificationPubSubPayload = {
    openOffCanvas?: boolean
}

export const OpenSearchClientByIdentificationPubSub = new PubSub<OpenSearchClientByIdentificationPubSubPayload>();

export type OpenCabysSearchPubSubPayload = {
    openOffCanvas?: boolean
}

export const OpenCabysSearchPubSub = new PubSub<OpenCabysSearchPubSubPayload>();

export type OpenAddClientPubSubPayload = {
    openOffCanvas?: boolean
}

export const OpenAddClientPubSub = new PubSub<OpenAddClientPubSubPayload>();

export type AddClientToDocumentPubSubPayload = {
    client: ClientDataType
}

export const AddClientToDocumentPubSub = new PubSub<AddClientToDocumentPubSubPayload>();

export type OpenProductPartidaArancelariaPubSubPayload = {
    openOffCanvas?: boolean,
    productLineId: string,
    partidaArancelaria: string,
}

export const OpenProductPartidaArancelariaPubSub = new PubSub<OpenProductPartidaArancelariaPubSubPayload>();

export type OpenProductTaxChangePubSubPayload = {
    openOffCanvas?: boolean,
    defaultData?: ProductTaxChangeDataType
}

export const OpenProductTaxChangePubSub = new PubSub<OpenProductTaxChangePubSubPayload>();

export type OpenChangeDocumentPlazoPubSubPayload = {
    openOffCanvas?: boolean,
    documentId: string,
    documentType: "invoice" | "settlement"
    plazo: number
}

export const OpenChangeDocumentPlazoPubSub = new PubSub<OpenChangeDocumentPlazoPubSubPayload>();

export type OpenAssignCreditnotePubSubPayload = {
    openOffCanvas?: boolean,
    creditNote: CreditnoteDataType
}

export const OpenAssignCreditnotePubSub = new PubSub<OpenAssignCreditnotePubSubPayload>();


export type OpenSetInvoiceDiscountTypePubSubPayload = {
    openOffCanvas?: boolean,
    discountType: string
}

export const OpenSetInvoiceDiscountTypePubSub = new PubSub<OpenSetInvoiceDiscountTypePubSubPayload>();

export type OpenDocumentExemptionPubSubPayload = {
    openOffCanvas?: boolean,
    productId?: string,
    defaultData?: Partial<ProductExemptionDataType>,
    lineSubtotal: number
}

export const OpenDocumentExemptionPubSub = new PubSub<OpenDocumentExemptionPubSubPayload>();

export type LoadInvoiceTemplatePubSubPayload = {
    invoiceTemplate?: InvoiceTemplateDataType
}

export const LoadInvoiceTemplatePubSub = new PubSub<LoadInvoiceTemplatePubSubPayload>();

export type OpenSendDocumentPubSubPayload = {
    openOffCanvas?: boolean,
    sourceURL?: string,
    emails?: string,
    emailProps?: EmailProps,
}

export const OpenSendDocumentPubSub = new PubSub<OpenSendDocumentPubSubPayload>();

export type OpenCancelInvoicePubSubPayload = {
    openOffCanvas?: boolean,
    invoice?: InvoiceDataType,
}

export const OpenCancelInvoicePubSub = new PubSub<OpenCancelInvoicePubSubPayload>();

export type OpenSendDocumentMessageAsPubSubPayload = {
    openOffCanvas?: boolean,
    invoiceAttachmentId: string,
}

export const OpenSendDocumentMessageAsPubSub = new PubSub<OpenSendDocumentMessageAsPubSubPayload>();

export type OpenViewRecievedEmailPubSubPayload = {
    openOffCanvas?: boolean,
    emailId: string,
}

export const OpenViewRecievedEmailPubSub = new PubSub<OpenViewRecievedEmailPubSubPayload>();

export type OpenInvoiceRecibePubSubPayload = {
    openOffCanvas?: boolean,
    recibeTotal: number,
    recibeDate: string,
    selectedDocuments: DocumentsEntryType[],
    recibeCurrency: CurrencyDataType
    interestExemptedDocuments: string[]
}

export type CloseInvoiceRecibePubSubPayload = {}

export const CloseInvoiceRecibePubSub = new PubSub<CloseInvoiceRecibePubSubPayload>();

export const OpenInvoiceRecibePubSub = new PubSub<OpenInvoiceRecibePubSubPayload>();

export type OpenAddCreditAmountPubSubPayload = {
    openOffCanvas?: boolean,
    clientId: string,
    clientNombre: string,
    documents: CreditAmountTarget[],
    currency: CurrencyDataType,
    date: string,
}

export type CloseAddCreditAmountPubSubPayload = {}

export const OpenAddCreditAmountPubSub = new PubSub<OpenAddCreditAmountPubSubPayload>();

export const CloseAddCreditAmountPubSub = new PubSub<CloseAddCreditAmountPubSubPayload>();

export type OpenProviderInvoiceRecibePubSubPayload = {
    openOffCanvas?: boolean,
    recibeTotal: number,
    recibeDate: string,
    selectedDocuments: DocumentsEntryType[],
    recibeCurrency: CurrencyDataType
}

export const OpenProviderInvoiceRecibePubSub = new PubSub<OpenProviderInvoiceRecibePubSubPayload>();

export type CloseProviderInvoiceRecibePubSubPayload = {}

export const CloseProviderInvoiceRecibePubSub = new PubSub<CloseProviderInvoiceRecibePubSubPayload>();

export type ScrollToDiscountCodeSelectPubSubPayload = {}

export const ScrollToDiscountCodeSelectPubSub = new PubSub<ScrollToDiscountCodeSelectPubSubPayload>();

export type OpenSendAtvDocumentPubSubPayload = {
    openOffCanvas?: boolean,
    documentId?: string,
    documentKind: "Invoice" | "Creditnote" | "Debitnote",
    suscription_numero: number,
    documentkey: string,
    clientEmail: string,
    emailBody: string,
    emailSubject: string,
    Suscription: {
        email: string,
        use_easypos_email?: boolean
    }
}

export const OpenSendAtvDocumentPubSub = new PubSub<OpenSendAtvDocumentPubSubPayload>();

export type ConfirmPrintPubSubPayload = {
    openOffCanvas: boolean,
    printVersionUrl?: string,
    /**
     * Documentos que se imprimen junto con el principal (por ejemplo la nota de credito
     * del descuento de una factura). Solo aplica al formato carta: en tiquete la
     * impresion la resuelve `ticketPrintCallBack`.
     */
    extraPrintUrls?: string[],
    ticketPrintCallBack?: () => void,
}

export const ConfirmPrintPubSub = new PubSub<ConfirmPrintPubSubPayload>();

export type ConfirmActionPubSubPayload = {
    openOffCanvas: boolean,
    onConfirmCalback?: () => void,
    onCancelCalback?: () => void,
    confirmText: string | ReactNode
}

export const ConfirmActionPubSub = new PubSub<ConfirmActionPubSubPayload>();

export type PrintPageDataLoadedPubSubPayload = {
    dataLoaded: boolean,
}

export const PrintPageDataLoadedPubSub = new PubSub<PrintPageDataLoadedPubSubPayload>();


export type OpenInvoicePaymentPubSubPayload = {
    openOffCanvas?: boolean,
    pendingAmount: number,
    invoiceId: string,
    invoiceNumero: number,
    currencyId: string,
}

export const OpenInvoicePaymentPubSub = new PubSub<OpenInvoicePaymentPubSubPayload>();

export type CloseInvoicePaymentPubSubPayload = {}

export const CloseInvoicePaymentPubSub = new PubSub<CloseInvoicePaymentPubSubPayload>();

export type OpenListPriceGeneratorPubSubPayload = {
    openOffCanvas?: boolean
}

export const OpenListPriceGeneratorPubSub = new PubSub<OpenListPriceGeneratorPubSubPayload>();

export type OpenClientsReportPubSubPayload = {
    openOffCanvas?: boolean
}

export const OpenClientsReportPubSub = new PubSub<OpenClientsReportPubSubPayload>();

export type OpenProvidersReportPubSubPayload = {
    openOffCanvas?: boolean
}

export const OpenProvidersReportPubSub = new PubSub<OpenProvidersReportPubSubPayload>();

export type OpenSendBackupToEmailPubSubPayload = {
    openOffCanvas?: boolean,
    toEmail?: string
}

export const OpenSendBackupToEmailPubSub = new PubSub<OpenSendBackupToEmailPubSubPayload>();

export type OpenAccountingPackagePubSubPayload = {
    openOffCanvas?: boolean,
    /** "month" pide mes+año; "year" solo año. Es lo unico que distingue a los dos paquetes. */
    mode?: AccountingPackageMode,
    toEmail?: string
}

export const OpenAccountingPackagePubSub = new PubSub<OpenAccountingPackagePubSubPayload>();

/**
 * Emitido por `ReportPage` cuando arma el Excel de un reporte montado en un portal
 * de exportacion (`renderRouteToExcel`), en vez de descargarlo con `saveAs`.
 */
export type ReportExcelExportedPubSubPayload = {
    exportId: string,
    fileName: string,
    bytes: Uint8Array
}

export const ReportExcelExportedPubSub = new PubSub<ReportExcelExportedPubSubPayload>();

export type DocumentStatusListPubSubPayload = {
    action: "Clear"
}

export const DocumentStatusListPubSub = new PubSub<DocumentStatusListPubSubPayload>();

export type OpenDocumentReferencesPubSubPayload = {
    openOffCanvas?: boolean,
    readonly?: boolean,
    documentKind?: "Invoice" | "BuyInvoice" | "Creditnote" | "Debitnote",
}

export const OpenDocumentReferencesPubSub = new PubSub<OpenDocumentReferencesPubSubPayload>();

export type OpenDocumentOtherChargesPubSubPayload = {
    openOffCanvas?: boolean,
    readonly?: boolean,
    documentKind?: "Invoice" | "BuyInvoice" | "Creditnote" | "Debitnote" | "Quote",
}
export const OpenDocumentOtherChargesPubSub = new PubSub<OpenDocumentOtherChargesPubSubPayload>();

export type OpenShippingCostPubSubPayload = {
    openOffCanvas?: boolean,
    documentKind?: "Invoice" | "Quote",
}
export const OpenShippingCostPubSub = new PubSub<OpenShippingCostPubSubPayload>();

/** Sincroniza ediciones locales de líneas de producto a Redux antes de guardar el documento. */
export const FlushDocumentProductEditsPubSub = new PubSub<void>();

export type OpenUpgradePlanPubSubPayload = {
    /** Nombre amigable del feature bloqueado, para mostrar en el mensaje */
    featureLabel?: string
    /** Id estable del feature bloqueado, para resaltar el plan recomendado */
    featureId?: string
}

export const OpenUpgradePlanPubSub = new PubSub<OpenUpgradePlanPubSubPayload>();